import { useState, useEffect, useRef } from 'react'
import { User, Upload, Save, RefreshCw, Wifi, WifiOff, AlertCircle, Download, FileUp, Archive, Trash2, Clock, Users } from 'lucide-react'
import { useP2PStore } from '../../store/p2pStore'
import { SelfTA } from '../../types'
import { AvatarService, AvatarBackup } from '../../services/AvatarService'
import { LocalStorageService } from '../../services/LocalStorageService'
import { ConnectionWizard } from './ConnectionWizard'
import { toastManager, TOAST_GROUPS } from '../../utils/toastManager'
import { useAvatarPreview } from '../../contexts/AvatarPreviewContext'

export function AvatarPage() {
  const { setAvatarPreview } = useAvatarPreview()
  const [avatarData, setAvatarData] = useState({
    alias: '',
    displayName: '',
    statusMessage: '',
    profileImage: null as string | null,
  })
  const [showBackupManager, setShowBackupManager] = useState(false)
  const [showConnectionWizard, setShowConnectionWizard] = useState(false)
  const [backups, setBackups] = useState<AvatarBackup[]>([])
  const [isProcessing, setIsProcessing] = useState(false)
  const [lastSession, setLastSession] = useState<{ peerId: string; alias: string; timestamp: Date } | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  // Direct store access for maximum stability (avoid multiple hook calls)
  const store = useP2PStore()
  const {
    localSelfta,
    initializeNetwork,
    updateSelfta,
    saveAvatarDataLocally,
    shutdownNetwork,
    isInitializing,
    restoreNetworkState,
    isOnline,
    isNetworkStabilized,
    connectionProgress,
    connections,
    networkError
  } = store

  // Derived connection status
  const connectionStatus = {
    isOnline,
    isInitializing,
    isNetworkStabilized,
    connectionProgress,
    connectionCount: Array.from(connections.values()).length,
    error: networkError,
    hasError: !!networkError
  }

  // Derived network stats
  const networkStats = {
    peerId: localSelfta?.id || '',
    alias: localSelfta?.alias || '',
    connectionCount: Array.from(connections.values()).length,
    isStable: isNetworkStabilized,
    totalMessages: 0, // TODO: Implement message tracking
    totalConnections: Array.from(connections.values()).length,
    lastActivity: new Date()
  }

  const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (file) {
      const reader = new FileReader()
      reader.onload = (e) => {
        const newImage = e.target?.result as string
        setAvatarData(prev => ({
          ...prev,
          profileImage: newImage
        }))
        // Update preview context for real-time header sync
        setAvatarPreview({
          profileImage: newImage,
          displayName: avatarData.displayName,
          alias: avatarData.alias
        })

        // Auto-save image to store to prevent loss on navigation
        if (localSelfta && avatarData.alias && avatarData.displayName) {
          const autoSaveData = {
            alias: avatarData.alias,
            displayName: avatarData.displayName,
            statusMessage: avatarData.statusMessage,
            profileImage: newImage
          }
          saveAvatarDataLocally(autoSaveData)
          console.log('🔄 Auto-saved avatar image to prevent loss on navigation')
        }
      }
      reader.readAsDataURL(file)
    }
  }

  // CRITICAL FIX: Simplified avatar loading that relies on store rehydration
  // This eliminates race conditions and ensures consistent state
  useEffect(() => {
    // Only update local form state when store data changes
    // The store handles all persistence logic during rehydration
    if (localSelfta) {
      console.log('📂 AvatarPage: Updating form from store data')

      setAvatarData({
        alias: localSelfta.alias,
        displayName: localSelfta.displayName,
        statusMessage: localSelfta.statusMessage,
        profileImage: localSelfta.profileImage ?? null, // Convert undefined to null
      })

      // Update preview context when loading from store
      setAvatarPreview({
        profileImage: localSelfta.profileImage ?? null,
        displayName: localSelfta.displayName,
        alias: localSelfta.alias
      })

      // Debug log to verify image data
      if (localSelfta.profileImage) {
        console.log('✅ AvatarPage: Profile image loaded from store:', {
          hasImage: true,
          imageSize: localSelfta.profileImage.length,
          imageType: localSelfta.profileImage.substring(5, 15), // e.g., "image/png"
          isValidFormat: /^data:image\/(png|jpe?g|gif|svg\+xml|webp);base64,/.test(localSelfta.profileImage)
        })
      } else {
        console.log('ℹ️ AvatarPage: No profile image in store data')
      }
    } else {
      console.log('ℹ️ AvatarPage: No avatar data in store yet (waiting for rehydration...)')
    }
  }, [localSelfta, setAvatarPreview]) // Only depend on localSelfta changes

  // Load backups and last session on component mount
  useEffect(() => {
    loadBackups()
    
    // Load last session info
    const session = LocalStorageService.loadLastSession()
    setLastSession(session)
  }, [])

  // Restore network state on component mount
  useEffect(() => {
    restoreNetworkState()
  }, [restoreNetworkState])

  const loadBackups = () => {
    const availableBackups = AvatarService.getBackups()
    setBackups(availableBackups)
  }

  // Initialize P2P network with avatar (new registration)
  const handleInitializeNetwork = async () => {
    if (!avatarData.alias.trim() || !avatarData.displayName.trim()) {
      toastManager.error('Please enter both alias and display name', TOAST_GROUPS.AVATAR)
      return
    }

    try {
      const selfta: SelfTA = {
        id: '', // Will be assigned by PeerJS
        alias: avatarData.alias.trim(),
        displayName: avatarData.displayName.trim(),
        statusMessage: avatarData.statusMessage.trim(),
        profileImage: avatarData.profileImage,
        createdAt: new Date(),
        lastUpdated: new Date()
      }

      // Save avatar data to localStorage before connecting
      LocalStorageService.saveAvatarData({
        alias: selfta.alias,
        displayName: selfta.displayName,
        statusMessage: selfta.statusMessage,
        profileImage: selfta.profileImage || null
      })

      const success = await initializeNetwork(selfta)
      
      if (success) {
        toastManager.success('P2P network initialized successfully!', TOAST_GROUPS.LOGIN)
      }
    } catch (error) {
      console.error('Network initialization error:', error)
      toastManager.error('Failed to initialize P2P network', TOAST_GROUPS.LOGIN)
    }
  }

  // Relogin with preserved alias and new peerId
  const handleRelogin = async () => {
    const reloginData = LocalStorageService.prepareReloginAvatar()
    if (!reloginData) {
      toastManager.error('No previous session found', TOAST_GROUPS.LOGIN)
      return
    }

    try {
      const selfta: SelfTA = {
        id: '', // New peerId will be assigned by PeerJS
        alias: reloginData.alias, // Preserved alias
        displayName: reloginData.displayName,
        statusMessage: reloginData.statusMessage,
        profileImage: reloginData.profileImage || null,
        createdAt: new Date(), // Fresh session
        lastUpdated: new Date()
      }

      console.log(`🔄 Relogin with preserved alias: ${reloginData.alias}`)
      toastManager.success(`Relogin with alias: ${reloginData.alias}`, TOAST_GROUPS.LOGIN)

      const success = await initializeNetwork(selfta)
      
      if (success) {
        toastManager.success('P2P network reconnected with preserved alias!', TOAST_GROUPS.LOGIN)
      }
    } catch (error) {
      console.error('Relogin error:', error)
      toastManager.error('Failed to relogin to P2P network', TOAST_GROUPS.LOGIN)
    }
  }

  // Update existing avatar
  const handleSave = () => {
    if (!localSelfta) {
      toastManager.error('Please initialize your P2P network first', TOAST_GROUPS.AVATAR)
      return
    }

    if (!avatarData.alias.trim() || !avatarData.displayName.trim()) {
      toastManager.error('Please enter both alias and display name', TOAST_GROUPS.AVATAR)
      return
    }

    const updates: Partial<SelfTA> = {
      alias: avatarData.alias.trim(),
      displayName: avatarData.displayName.trim(),
      statusMessage: avatarData.statusMessage.trim(),
      profileImage: avatarData.profileImage,
    }

    console.log('🔄 Updating avatar with:', {
      alias: updates.alias,
      hasImage: !!updates.profileImage,
      imageSize: updates.profileImage ? updates.profileImage.length : 0
    })

    updateSelfta(updates)

    // Always save to localStorage after updating store
    const saveResult = LocalStorageService.saveAvatarData({
      alias: updates.alias!,
      displayName: updates.displayName!,
      statusMessage: updates.statusMessage!,
      profileImage: updates.profileImage || null
    })

    if (saveResult) {
      // Clear preview after successful save as store data is now current
      setAvatarPreview(null)
      toastManager.success('Avatar updated and saved locally!', TOAST_GROUPS.AVATAR)
    } else {
      toastManager.error('Avatar updated but failed to save locally!', TOAST_GROUPS.AVATAR)
    }
  }

  // Export current avatar
  const handleExportAvatar = async () => {
    if (!localSelfta) {
      toastManager.error('No avatar to export. Please initialize your P2P network first.', TOAST_GROUPS.AVATAR)
      return
    }

    setIsProcessing(true)
    try {
      await AvatarService.exportAvatar(localSelfta)
      toastManager.success('Avatar exported successfully!', TOAST_GROUPS.AVATAR)
    } catch (error) {
      console.error('Export error:', error)
      toastManager.error(error instanceof Error ? error.message : 'Failed to export avatar', TOAST_GROUPS.AVATAR)
    } finally {
      setIsProcessing(false)
    }
  }

  // Import avatar from file
  const handleImportAvatar = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) return

    setIsProcessing(true)
    AvatarService.importAvatar(file)
      .then(importedAvatar => {
        // Validate imported avatar
        const validation = AvatarService.validateAvatar(importedAvatar)
        if (!validation.valid) {
          throw new Error(`Invalid avatar data: ${validation.errors.join(', ')}`)
        }

        // Update form with imported data
        setAvatarData({
          alias: importedAvatar.alias,
          displayName: importedAvatar.displayName,
          statusMessage: importedAvatar.statusMessage,
          profileImage: importedAvatar.profileImage || null,
        })
        
        toastManager.success(`Avatar "${importedAvatar.displayName}" imported successfully!`, TOAST_GROUPS.AVATAR)
      })
      .catch(error => {
        console.error('Import error:', error)
        toastManager.error(error instanceof Error ? error.message : 'Failed to import avatar', TOAST_GROUPS.AVATAR)
      })
      .finally(() => {
        setIsProcessing(false)
        // Clear file input
        if (fileInputRef.current) {
          fileInputRef.current.value = ''
        }
      })
  }

  // Create backup of current avatar
  const handleBackupAvatar = async () => {
    if (!localSelfta) {
      toastManager.error('No avatar to backup. Please initialize your P2P network first.', TOAST_GROUPS.AVATAR)
      return
    }

    setIsProcessing(true)
    try {
      await AvatarService.backupAvatar(localSelfta)
      loadBackups() // Refresh backup list
      toastManager.success('Avatar backup created successfully!', TOAST_GROUPS.AVATAR)
    } catch (error) {
      console.error('Backup error:', error)
      toastManager.error(error instanceof Error ? error.message : 'Failed to create backup', TOAST_GROUPS.AVATAR)
    } finally {
      setIsProcessing(false)
    }
  }

  // Restore avatar from backup
  const handleRestoreBackup = async (backupId: string) => {
    setIsProcessing(true)
    try {
      const restoredAvatar = await AvatarService.restoreBackup(backupId)
      
      // Update form with restored data
      setAvatarData({
        alias: restoredAvatar.alias,
        displayName: restoredAvatar.displayName,
        statusMessage: restoredAvatar.statusMessage,
        profileImage: restoredAvatar.profileImage || null,
      })
      
      toastManager.success(`Avatar restored from backup!`, TOAST_GROUPS.AVATAR)
    } catch (error) {
      console.error('Restore error:', error)
      toastManager.error(error instanceof Error ? error.message : 'Failed to restore backup', TOAST_GROUPS.AVATAR)
    } finally {
      setIsProcessing(false)
    }
  }

  // Delete backup
  const handleDeleteBackup = async (backupId: string) => {
    if (!confirm('Are you sure you want to delete this backup?')) return
    
    if (AvatarService.deleteBackup(backupId)) {
      loadBackups() // Refresh backup list
      toastManager.success('Backup deleted successfully', TOAST_GROUPS.AVATAR)
    } else {
      toastManager.error('Failed to delete backup', TOAST_GROUPS.AVATAR)
    }
  }

  // Generate random avatar data with SVG image
  const generateRandomAvatar = () => {
    const adjectives = ['Cool', 'Smart', 'Swift', 'Bright', 'Bold', 'Clever', 'Quick', 'Wise']
    const nouns = ['Node', 'Peer', 'User', 'Agent', 'Link', 'Hub', 'Point', 'Net']
    const randomAdjective = adjectives[Math.floor(Math.random() * adjectives.length)] || 'Cool'
    const randomNoun = nouns[Math.floor(Math.random() * nouns.length)] || 'User'
    const randomNumber = Math.floor(Math.random() * 999) + 1

    // Generate colorful SVG avatar
    const colors = [
      '#3b82f6', '#ef4444', '#10b981', '#f59e0b', '#8b5cf6',
      '#ec4899', '#06b6d4', '#84cc16', '#f97316', '#6366f1'
    ]
    const randomColor = colors[Math.floor(Math.random() * colors.length)]
    const initials = `${randomAdjective[0]}${randomNoun[0]}`

    // Create SVG avatar using LocalStorageService utility
    const avatarSVG = LocalStorageService.createAvatarSVG(initials, randomColor, '#ffffff')

    const newAvatar = {
      alias: `${randomAdjective.toLowerCase()}_${randomNoun.toLowerCase()}_${randomNumber}`,
      displayName: `${randomAdjective} ${randomNoun} ${randomNumber}`,
      statusMessage: 'Ready for P2P connections',
      profileImage: avatarSVG
    }

    setAvatarData(prev => ({
      ...prev,
      ...newAvatar
    }))

    // Update preview for real-time header sync
    setAvatarPreview({
      profileImage: newAvatar.profileImage,
      displayName: newAvatar.displayName,
      alias: newAvatar.alias
    })

    // Auto-save to store to prevent loss on navigation
    saveAvatarDataLocally(newAvatar)

    toastManager.info('Random avatar generated!', TOAST_GROUPS.AVATAR, { icon: '🎲' })
  }

  // Handle network shutdown
  const handleShutdownNetwork = () => {
    shutdownNetwork()
    toastManager.info('P2P network disconnected', TOAST_GROUPS.AVATAR, { icon: '🔌' })
  }

  return (
    <div className="p-4 sm:p-6 max-w-2xl mx-auto mobile-prevent-overflow">
      <div className="mb-8">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-3xl font-bold text-secondary-900 mb-2">
              Avatar Management
            </h1>
            <p className="text-secondary-600">
              Create and customize your digital identity for the useSelft network
            </p>
          </div>
          
          {/* Network Status Indicator */}
          <div className="flex items-center space-x-2">
            {connectionStatus.isOnline ? (
              <>
                <Wifi className="w-5 h-5 text-success-600" />
                <div className="text-sm">
                  <div className="text-success-600 font-medium">Online</div>
                  <div className="text-secondary-500">
                    {connectionStatus.connectionCount} connections
                  </div>
                </div>
              </>
            ) : isInitializing ? (
              <>
                <div className="w-5 h-5 animate-spin border-2 border-primary-500 border-t-transparent rounded-full" />
                <div className="text-sm">
                  <div className="text-primary-600 font-medium">Connecting...</div>
                  <div className="text-secondary-500">Initializing network</div>
                </div>
              </>
            ) : (
              <>
                <WifiOff className="w-5 h-5 text-secondary-400" />
                <div className="text-sm">
                  <div className="text-secondary-600 font-medium">Offline</div>
                  <div className="text-secondary-500">Not connected</div>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Network Error Display */}
        {connectionStatus.hasError && (
          <div className="bg-error-50 border border-error-200 rounded-lg p-4 mb-4">
            <div className="flex items-center space-x-2">
              <AlertCircle className="w-4 h-4 text-error-600" />
              <span className="text-error-800 font-medium">Network Error</span>
            </div>
            <p className="text-error-700 text-sm mt-1">
              There was an issue with the P2P network. Please try reinitializing.
            </p>
          </div>
        )}

        {/* Last Session Info Display */}
        {lastSession && !connectionStatus.isOnline && (
          <div className="bg-primary-50 border border-primary-200 rounded-lg p-4 mb-4">
            <div className="flex items-center space-x-2">
              <Clock className="w-4 h-4 text-primary-600" />
              <span className="text-primary-800 font-medium">Previous Session</span>
            </div>
            <p className="text-primary-700 text-sm mt-1">
              Last connected as <strong>{lastSession.alias}</strong> on {lastSession.timestamp.toLocaleString()}
            </p>
            <p className="text-primary-600 text-xs mt-1">
              Use "Relogin" to reconnect with the same alias but a new session ID
            </p>
          </div>
        )}
      </div>

      <div className="card">
        <div className="card-header">
          <h2 className="card-title">Your Avatar</h2>
          <p className="card-description">
            Customize how you appear to other peers in the network
          </p>
        </div>

        <div className="card-content space-y-6">
          {/* Profile Image Section */}
          <div className="flex flex-col items-center space-y-4">
            <div className="relative">
              {avatarData.profileImage ? (
                <img
                  src={avatarData.profileImage}
                  alt="Profile"
                  className="w-24 h-24 rounded-full object-contain bg-gray-100 border-4 border-secondary-200 p-1"
                />
              ) : (
                <div className="w-24 h-24 bg-secondary-200 rounded-full flex items-center justify-center border-4 border-secondary-200">
                  <User className="w-10 h-10 text-secondary-500" />
                </div>
              )}
              
              <label className="absolute bottom-0 right-0 bg-primary-600 text-white p-2 rounded-full cursor-pointer hover:bg-primary-700 transition-colors">
                <Upload className="w-4 h-4" />
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageUpload}
                  className="hidden"
                />
              </label>
            </div>

            <button
              onClick={generateRandomAvatar}
              className="btn btn-outline btn-sm"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Generate Random
            </button>
          </div>

          {/* Form Fields */}
          <div className="space-y-4">
            <div>
              <label htmlFor="alias-input" className="block text-sm font-medium text-secondary-700 mb-2">
                Alias (Unique Identifier)
              </label>
              <input
                id="alias-input"
                type="text"
                value={avatarData.alias}
                onChange={(e) => {
                  const newAlias = e.target.value
                  setAvatarData(prev => ({
                    ...prev,
                    alias: newAlias
                  }))
                  // Update preview context for real-time header sync
                  setAvatarPreview({
                    profileImage: avatarData.profileImage,
                    displayName: avatarData.displayName,
                    alias: newAlias
                  })
                }}
                placeholder="e.g., alice_node"
                className="input"
                maxLength={20}
              />
              <p className="text-xs text-secondary-500 mt-1">
                This is how other peers will find you. Must be unique.
              </p>
            </div>

            <div>
              <label htmlFor="display-name-input" className="block text-sm font-medium text-secondary-700 mb-2">
                Display Name
              </label>
              <input
                id="display-name-input"
                type="text"
                value={avatarData.displayName}
                onChange={(e) => {
                  const newName = e.target.value
                  setAvatarData(prev => ({
                    ...prev,
                    displayName: newName
                  }))
                  // Update preview context for real-time header sync
                  setAvatarPreview({
                    profileImage: avatarData.profileImage,
                    displayName: newName,
                    alias: avatarData.alias
                  })
                }}
                placeholder="e.g., Alice Johnson"
                className="input"
                maxLength={50}
              />
              <p className="text-xs text-secondary-500 mt-1">
                A friendly name that others will see in conversations.
              </p>
            </div>

            <div>
              <label htmlFor="status-message-input" className="block text-sm font-medium text-secondary-700 mb-2">
                Status Message
              </label>
              <textarea
                id="status-message-input"
                value={avatarData.statusMessage}
                onChange={(e) => setAvatarData(prev => ({
                  ...prev,
                  statusMessage: e.target.value
                }))}
                placeholder="e.g., Available for P2P connections"
                className="input min-h-[80px] resize-none"
                maxLength={100}
                rows={3}
              />
              <p className="text-xs text-secondary-500 mt-1">
                {avatarData.statusMessage.length}/100 characters
              </p>
            </div>
          </div>
        </div>

        <div className="card-footer flex flex-col sm:flex-row flex-wrap gap-3">
          {/* Local Save Button (always available) */}
          <button
            onClick={() => {
              if (!avatarData.alias.trim() || !avatarData.displayName.trim()) {
                toastManager.error('Please enter both alias and display name', TOAST_GROUPS.AVATAR)
                return
              }

              // CRITICAL FIX: Enhanced save with validation and mobile support
              const cleanedData = {
                alias: avatarData.alias.trim(),
                displayName: avatarData.displayName.trim(),
                statusMessage: avatarData.statusMessage.trim(),
                profileImage: avatarData.profileImage
              }

              console.log('💾 Saving avatar data locally:', {
                alias: cleanedData.alias,
                hasImage: !!cleanedData.profileImage,
                imageSize: cleanedData.profileImage?.length || 0
              })

              // Save to both localStorage and Zustand store atomically
              const saveResult = saveAvatarDataLocally(cleanedData)

              if (saveResult) {
                toastManager.success('Avatar data saved locally!', TOAST_GROUPS.AVATAR)
                // Avatar data saved successfully
              } else {
                toastManager.error('Failed to save avatar data locally', TOAST_GROUPS.AVATAR)
                // Failed to save avatar data locally
              }
            }}
            className="btn btn-outline w-full sm:w-auto mobile-touch-target"
            disabled={!avatarData.alias.trim() || !avatarData.displayName.trim()}
          >
            <Save className="w-4 h-4 mr-2" />
            <span className="hidden sm:inline">Save </span>Locally
          </button>
          {connectionStatus.isOnline ? (
            <>
              <button
                onClick={handleSave}
                className="btn btn-primary w-full sm:w-auto mobile-touch-target"
                disabled={!avatarData.alias.trim() || !avatarData.displayName.trim()}
              >
                <Save className="w-4 h-4 mr-2" />
                <span className="hidden sm:inline">Update & </span>Sync Avatar
              </button>

              <button
                onClick={() => setShowConnectionWizard(true)}
                className="btn btn-outline btn-success w-full sm:w-auto mobile-touch-target"
              >
                <Users className="w-4 h-4 mr-2" />
                <span className="hidden sm:inline">Connect to </span>Peers
              </button>

              <button
                onClick={handleShutdownNetwork}
                className="btn btn-outline text-error-600 border-error-300 hover:bg-error-50 w-full sm:w-auto mobile-touch-target"
              >
                <WifiOff className="w-4 h-4 mr-2" />
                <span className="hidden sm:inline">Disconnect </span>Network
              </button>
            </>
          ) : (
            <>
              {/* Show relogin option if user has existing session */}
              {LocalStorageService.hasExistingSession() ? (
                <>
                  <button
                    onClick={handleRelogin}
                    disabled={isInitializing}
                    className="btn btn-primary disabled:opacity-50 disabled:cursor-not-allowed w-full sm:w-auto mobile-touch-target"
                  >
                    {isInitializing ? (
                      <>
                        <div className="w-4 h-4 animate-spin border-2 border-white border-t-transparent rounded-full mr-2" />
                        Reconnecting...
                      </>
                    ) : (
                      <>
                        <RefreshCw className="w-4 h-4 mr-2" />
                        <span className="hidden sm:inline">Relogin (</span><span className="sm:hidden">Relogin</span><span className="hidden sm:inline">{LocalStorageService.getLastKnownAlias() || 'preserved alias'})</span>
                      </>
                    )}
                  </button>

                  <button
                    onClick={handleInitializeNetwork}
                    disabled={isInitializing || !avatarData.alias.trim() || !avatarData.displayName.trim()}
                    className="btn btn-outline disabled:opacity-50 disabled:cursor-not-allowed w-full sm:w-auto mobile-touch-target"
                  >
                    <Wifi className="w-4 h-4 mr-2" />
                    <span className="hidden sm:inline">New </span>Registration
                  </button>
                </>
              ) : (
                <button
                  onClick={handleInitializeNetwork}
                  disabled={isInitializing || !avatarData.alias.trim() || !avatarData.displayName.trim()}
                  className="btn btn-primary disabled:opacity-50 disabled:cursor-not-allowed w-full sm:w-auto mobile-touch-target"
                >
                  {isInitializing ? (
                    <>
                      <div className="w-4 h-4 animate-spin border-2 border-white border-t-transparent rounded-full mr-2" />
                      <span className="hidden sm:inline">Connecting to </span>Network...
                    </>
                  ) : (
                    <>
                      <Wifi className="w-4 h-4 mr-2" />
                      <span className="hidden sm:inline">Connect to P2P </span>Network
                    </>
                  )}
                </button>
              )}
            </>
          )}
          
          {/* Mobile-optimized secondary action group */}
          <div className="w-full sm:w-auto">
            <div className="grid grid-cols-2 sm:flex gap-2">
              <button
                onClick={handleExportAvatar}
                disabled={isProcessing || !localSelfta}
                className="btn btn-outline mobile-touch-target"
                title={localSelfta ? "Export avatar to file" : "No avatar to export"}
              >
                <Download className="w-4 h-4 sm:mr-2" />
                <span className="hidden sm:inline">{isProcessing ? 'Exporting...' : 'Export'}</span>
              </button>

              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={isProcessing}
                className="btn btn-outline mobile-touch-target"
              >
                <FileUp className="w-4 h-4 sm:mr-2" />
                <span className="hidden sm:inline">Import</span>
              </button>

              <button
                onClick={handleBackupAvatar}
                disabled={isProcessing || !localSelfta}
                className="btn btn-outline mobile-touch-target"
                title={localSelfta ? "Create backup of current avatar" : "No avatar to backup"}
              >
                <Archive className="w-4 h-4 sm:mr-2" />
                <span className="hidden sm:inline">{isProcessing ? 'Backing up...' : 'Backup'}</span>
              </button>

              <button
                onClick={() => setShowBackupManager(!showBackupManager)}
                className="btn btn-outline mobile-touch-target"
              >
                <Clock className="w-4 h-4 sm:mr-2" />
                <span className="hidden sm:inline">Manager </span>({backups.length})
              </button>
            </div>
          </div>
          
          {/* Hidden file input for import */}
          <input
            ref={fileInputRef}
            type="file"
            accept=".json"
            onChange={handleImportAvatar}
            className="hidden"
          />
        </div>
      </div>

      {/* Avatar Preview */}
      {(avatarData.alias || avatarData.displayName) && (
        <div className="mt-6 card">
          <div className="card-header">
            <h3 className="card-title">Preview</h3>
            <p className="card-description">
              How your avatar will appear to other peers
            </p>
          </div>
          
          <div className="card-content">
            <div className="flex items-center space-x-4 p-4 bg-secondary-50 rounded-lg">
              {avatarData.profileImage ? (
                <img
                  src={avatarData.profileImage}
                  alt="Preview"
                  className="w-12 h-12 rounded-full object-contain bg-gray-100 p-0.5"
                />
              ) : (
                <div className="w-12 h-12 bg-secondary-200 rounded-full flex items-center justify-center">
                  <User className="w-6 h-6 text-secondary-500" />
                </div>
              )}
              
              <div className="flex-1">
                <div className="font-medium text-secondary-900">
                  {avatarData.displayName || 'Display Name'}
                </div>
                <div className="text-sm text-secondary-600">
                  @{avatarData.alias || 'alias'}
                </div>
                {localSelfta && (
                  <div className="text-xs text-secondary-500 mt-1">
                    ID: {localSelfta.id.slice(0, 12)}...
                  </div>
                )}
                {avatarData.statusMessage && (
                  <div className="text-sm text-secondary-500 mt-1">
                    {avatarData.statusMessage}
                  </div>
                )}
              </div>
              
              <div className="text-right">
                <div className={`inline-flex items-center space-x-1 px-2 py-1 rounded-full text-xs font-medium ${
                  connectionStatus.isOnline 
                    ? 'bg-success-100 text-success-800' 
                    : 'bg-secondary-100 text-secondary-800'
                }`}>
                  <div className={`w-2 h-2 rounded-full ${
                    connectionStatus.isOnline ? 'bg-success-500' : 'bg-secondary-500'
                  }`} />
                  <span>{connectionStatus.isOnline ? 'Online' : 'Offline'}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Backup Manager */}
      {showBackupManager && (
        <div className="mt-6 card">
          <div className="card-header">
            <h3 className="card-title">Avatar Backup Manager</h3>
            <p className="card-description">
              Manage your avatar backups and restore previous versions
            </p>
          </div>
          
          <div className="card-content">
            {backups.length === 0 ? (
              <div className="text-center py-8 text-secondary-500">
                <Archive className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>No backups available</p>
                <p className="text-sm">Create a backup to get started</p>
              </div>
            ) : (
              <div className="space-y-3">
                {backups.map((backup) => (
                  <div key={backup.id} className="flex items-center justify-between p-4 bg-secondary-50 rounded-lg">
                    <div className="flex-1">
                      <div className="font-medium text-secondary-900">
                        {backup.avatar.displayName}
                      </div>
                      <div className="text-sm text-secondary-600">
                        @{backup.avatar.alias}
                      </div>
                      <div className="text-xs text-secondary-500 mt-1">
                        {backup.metadata.backupDate.toLocaleString()} • {backup.metadata.source}
                      </div>
                    </div>
                    
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => handleRestoreBackup(backup.id)}
                        disabled={isProcessing}
                        className="btn btn-sm btn-primary"
                        title="Restore this backup"
                      >
                        Restore
                      </button>
                      <button
                        onClick={() => handleDeleteBackup(backup.id)}
                        disabled={isProcessing}
                        className="btn btn-sm btn-outline text-error-600 border-error-300 hover:bg-error-50"
                        title="Delete this backup"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
            
            {backups.length > 0 && (
              <div className="mt-4 p-3 bg-warning-50 rounded-lg">
                <div className="text-sm text-warning-800">
                  <strong>Note:</strong> Only the last 10 backups are kept. Older backups are automatically removed.
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Last Session Info */}
      {!connectionStatus.isOnline && lastSession && (
        <div className="mt-6 card">
          <div className="card-header">
            <h3 className="card-title">Last Session</h3>
            <p className="card-description">
              Information from your previous P2P session
            </p>
          </div>
          
          <div className="card-content">
            <div className="bg-info-50 p-4 rounded-lg">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-info-800">Previous Alias:</span>
                <span className="text-sm text-info-700">@{lastSession.alias}</span>
              </div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-info-800">Peer ID:</span>
                <span className="text-xs font-mono text-info-600">{lastSession.peerId.slice(0, 20)}...</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-info-800">Last Connected:</span>
                <span className="text-xs text-info-600">{lastSession.timestamp.toLocaleString()}</span>
              </div>
            </div>
            
            <div className="mt-3 p-3 bg-warning-50 rounded-lg">
              <div className="text-sm text-warning-800">
                <strong>Note:</strong> Each session gets a new Peer ID. The alias can be reused to maintain your identity.
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Network Statistics */}
      {connectionStatus.isOnline && (
        <div className="mt-6 card">
          <div className="card-header">
            <h3 className="card-title">Network Statistics</h3>
            <p className="card-description">
              Your P2P network activity and statistics
            </p>
          </div>
          
          <div className="card-content">
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
              <div className="bg-primary-50 p-4 rounded-lg">
                <div className="text-2xl font-bold text-primary-600">
                  {connectionStatus.connectionCount}
                </div>
                <div className="text-sm text-primary-800">Active Connections</div>
              </div>
              
              <div className="bg-success-50 p-4 rounded-lg">
                <div className="text-2xl font-bold text-success-600">
                  {networkStats.totalMessages}
                </div>
                <div className="text-sm text-success-800">Messages Sent</div>
              </div>
              
              <div className="bg-info-50 p-4 rounded-lg">
                <div className="text-2xl font-bold text-info-600">
                  {networkStats.totalConnections}
                </div>
                <div className="text-sm text-info-800">Total Connections</div>
              </div>
              
              <div className="bg-warning-50 p-4 rounded-lg">
                <div className="text-2xl font-bold text-warning-600">
                  {networkStats.lastActivity 
                    ? Math.floor((Date.now() - networkStats.lastActivity.getTime()) / 1000 / 60)
                    : 0
                  }m
                </div>
                <div className="text-sm text-warning-800">Last Activity</div>
              </div>
            </div>
            
            {localSelfta && (
              <div className="mt-4 p-3 bg-secondary-50 rounded-lg">
                <div className="text-sm text-secondary-600 mb-1">Your Peer ID (shareable):</div>
                <div className="font-mono text-sm bg-white p-2 rounded border select-all">
                  {localSelfta.id}
                </div>
                <div className="text-xs text-secondary-500 mt-1">
                  Share this ID with others to let them connect to you
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Connection Wizard */}
      <ConnectionWizard
        isOpen={showConnectionWizard}
        onClose={() => setShowConnectionWizard(false)}
        initialStep="peer-discovery"
      />
    </div>
  )
}